import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import './Login.css';

const Login: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(searchParams.get('mode') !== 'register');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { login, register } = useUser();
  const redirectTo = searchParams.get('redirect') || '/';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (isLogin) {
        // Login
        await login(formData.email, formData.password);
        
        // Check if user is admin and redirect accordingly
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUser.role === 'ADMIN') {
          setSuccess('Admin login successful! Redirecting to admin dashboard...');
          const target = redirectTo.startsWith('/admin') ? redirectTo : '/admin/dashboard';
          setTimeout(() => {
            navigate(target);
          }, 1500);
        } else {
          setSuccess('Login successful! Redirecting...');
          // Non-admins can't follow an /admin redirect (they'd just get bounced back here)
          const target = redirectTo.startsWith('/admin') ? '/' : redirectTo;
          setTimeout(() => {
            navigate(target);
          }, 1500);
        }
        
      } else {
        // Register
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }

        if (formData.password.length < 6) {
          setError('Password must be at least 6 characters long');
          setLoading(false);
          return;
        }

        await register({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone || undefined,
          address: formData.address || undefined
        });
        
        setSuccess('Registration successful! Welcome to EcoFashion!');
        setTimeout(() => {
          navigate(redirectTo);
        }, 1500);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>{isLogin ? 'Welcome Back' : 'Join EcoFashion'}</h1>
            <p className="login-subtitle">
              {isLogin 
                ? 'Sign in to your vintage fashion account' 
                : 'Create your account and start your vintage journey'
              }
            </p>
          </div>
          
          <form className="login-form" onSubmit={handleSubmit}>
            {error && (
              <div className="error-message" style={{
                background: '#fee',
                color: '#c33',
                padding: '10px',
                borderRadius: '5px',
                marginBottom: '15px',
                border: '1px solid #fcc'
              }}>
                {error}
              </div>
            )}
            
            {success && (
              <div className="success-message" style={{
                background: '#efe',
                color: '#282',
                padding: '10px',
                borderRadius: '5px',
                marginBottom: '15px',
                border: '1px solid #cfc'
              }}>
                {success}
              </div>
            )}
            
            {!isLogin && (
              <>
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input 
                    type="text" 
                    id="firstName" 
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Enter your first name"
                    required 
                  />
                  <span className="form-icon">👤</span>
                </div>
                
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input 
                    type="text" 
                    id="lastName" 
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Enter your last name"
                    required 
                  />
                  <span className="form-icon">👤</span>
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Phone (Optional)</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                  />
                  <span className="form-icon">📱</span>
                </div>
                
                <div className="form-group">
                  <label htmlFor="address">Address (Optional)</label>
                  <input 
                    type="text" 
                    id="address" 
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter your address"
                  />
                  <span className="form-icon">🏠</span>
                </div>
              </>
            )}
            
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input 
                type="email" 
                id="email" 
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                required 
              />
              <span className="form-icon">📧</span>
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input 
                type="password" 
                id="password" 
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                required 
              />
              <span className="form-icon">🔒</span>
            </div>
            
            {!isLogin && (
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input 
                  type="password" 
                  id="confirmPassword" 
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm your password"
                  required 
                />
                <span className="form-icon">🔑</span>
              </div>
            )}

            {isLogin && (
              <div className="form-options">
                <label className="remember-me">
                  <input type="checkbox" />
                  <span className="checkmark"></span>
                  Remember me
                </label>
                <Link to="/forgot-password" className="forgot-password">
                  Forgot Password?
                </Link>
              </div>
            )}
            
            <button type="submit" className="submit-btn" disabled={loading}>
              <span>
                {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
              </span>
              {!loading && <span className="btn-icon">→</span>}
            </button>
          </form>

          <div className="social-login">
            <div className="divider">
              <span>Or continue with</span>
            </div>
            <div className="social-buttons">
              <button className="social-btn google">
                <span className="social-icon">G</span>
                Google
              </button>
              <button className="social-btn facebook">
                <span className="social-icon">f</span>
                Facebook
              </button>
            </div>
          </div>
          
          <div className="toggle-form">
            <p>
              {isLogin ? "Don't have an account?" : 'Already have an account?'}
              <button 
                type="button" 
                className="toggle-btn"
                onClick={() => setIsLogin(!isLogin)}
              >
                {isLogin ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </div>

          {!isLogin && (
            <div className="terms-notice">
              <p>
                By creating an account, you agree to our{' '}
                <Link to="/terms">Terms of Service</Link> and{' '}
                <Link to="/privacy">Privacy Policy</Link>
              </p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Login;